import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Star, Quote, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useApp } from '../context/AppContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function ReviewsPage() {
  usePageTitle('Reviews');
  const { user, getAuthHeaders } = useApp();
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ rating: 0, message: '', location: '' });
  const [hover, setHover] = useState(0);

  const fetchTestimonials = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/testimonials`);
      setTestimonials(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTestimonials(); }, [fetchTestimonials]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.rating < 1) { toast.error('Please select a star rating'); return; }
    if (!form.message.trim()) { toast.error('Please write your feedback'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/testimonials`, {
        rating: form.rating,
        message: form.message.trim(),
        location: form.location.trim() || null,
      }, { headers: getAuthHeaders() });
      toast.success('Thank you! Your review is awaiting approval.');
      setForm({ rating: 0, message: '', location: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not submit review');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="page-transition" data-testid="reviews-page">
      {/* Hero */}
      <section className="bg-[#0B1120] py-16 text-center px-4">
        <h1 className="text-4xl sm:text-5xl font-bold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
          Customer Reviews
        </h1>
        <p className="mt-4 text-gray-300 max-w-xl mx-auto">
          Real words from real VShoppe customers. Smart Choices, True Value.
        </p>
      </section>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid grid-cols-1 lg:grid-cols-3 gap-10">
        {/* Reviews list */}
        <div className="lg:col-span-2">
          {loading ? (
            <div className="text-center py-12 text-muted-foreground">Loading reviews...</div>
          ) : testimonials.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground" data-testid="no-reviews">
              <Quote className="h-12 w-12 mx-auto mb-4 text-muted-foreground/40" />
              No reviews yet. Be the first to share your experience!
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid="reviews-list">
              {testimonials.map((t) => (
                <div key={t.id} className="bg-white rounded-2xl border border-border p-6" data-testid={`review-${t.id}`}>
                  <div className="flex mb-3">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <Star key={s} className={`h-4 w-4 ${s <= t.rating ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`} />
                    ))}
                  </div>
                  <p className="text-gray-700 leading-relaxed mb-4">"{t.message}"</p>
                  <div>
                    <p className="font-semibold text-gray-900">{t.customer_name}</p>
                    {t.location && <p className="text-gray-500 text-sm">{t.location}</p>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Submission form */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-2xl border border-border p-6 sticky top-24" data-testid="review-form-card">
            <h2 className="text-lg font-bold mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
              Leave a Review
            </h2>
            {user ? (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label>Your Rating</Label>
                  <div className="flex gap-1 mt-2" data-testid="rating-input">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <button
                        key={s}
                        type="button"
                        onMouseEnter={() => setHover(s)}
                        onMouseLeave={() => setHover(0)}
                        onClick={() => setForm({ ...form, rating: s })}
                        data-testid={`star-${s}`}
                        className="transition-transform hover:scale-110"
                      >
                        <Star className={`h-7 w-7 ${s <= (hover || form.rating) ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`} />
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label htmlFor="message">Your Feedback</Label>
                  <Textarea
                    id="message"
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    rows={4}
                    placeholder="Tell us about your experience..."
                    className="mt-1"
                    data-testid="review-message-input"
                  />
                </div>
                <div>
                  <Label htmlFor="location">Location (optional)</Label>
                  <Input
                    id="location"
                    value={form.location}
                    onChange={(e) => setForm({ ...form, location: e.target.value })}
                    placeholder="e.g. Auckland, NZ"
                    className="mt-1"
                    data-testid="review-location-input"
                  />
                </div>
                <Button type="submit" disabled={submitting} className="w-full bg-[#EA580C] hover:bg-[#EA580C]/90 text-white rounded-full" data-testid="submit-review-btn">
                  {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                  {submitting ? 'Submitting...' : 'Submit Review'}
                </Button>
                <p className="text-xs text-muted-foreground text-center">Reviews appear after approval.</p>
              </form>
            ) : (
              <div className="text-center py-4" data-testid="login-prompt">
                <p className="text-sm text-muted-foreground mb-4">Please log in to leave a review.</p>
                <Link to="/login">
                  <Button className="bg-[#EA580C] hover:bg-[#EA580C]/90 text-white rounded-full w-full" data-testid="login-to-review-btn">
                    Log in to Review
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
